import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import type { CostDataPoint } from '../../lib/types'

interface Props {
  data: CostDataPoint[]
}

const COLORS = ['#6366f1', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6', '#ec4899']

export default function CostChart({ data }: Props) {
  // Group by period, with each model as a separate key
  const models = [...new Set(data.map((d) => d.model))]
  const grouped: Record<string, Record<string, number>> = {}
  for (const point of data) {
    if (!grouped[point.period]) grouped[point.period] = {}
    grouped[point.period][point.model] = point.total_cost
  }
  const chartData = Object.entries(grouped).map(([period, costs]) => ({
    period,
    ...costs,
  }))

  if (chartData.length === 0) {
    return <p className="text-text-muted text-sm">No cost data available.</p>
  }

  return (
    <ResponsiveContainer width="100%" height={300}>
      <LineChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
        <XAxis
          dataKey="period"
          tick={{ fill: 'var(--color-text-secondary)', fontSize: 12 }}
          tickLine={false}
        />
        <YAxis
          tick={{ fill: 'var(--color-text-secondary)', fontSize: 12 }}
          tickLine={false}
          tickFormatter={(v: number) => `$${v.toFixed(2)}`}
        />
        <Tooltip
          contentStyle={{
            backgroundColor: 'var(--color-elevated)',
            border: '1px solid var(--color-surface)',
            borderRadius: 8,
            color: 'var(--color-text-primary)',
          }}
          formatter={(value: number) => [`$${value.toFixed(4)}`, undefined]}
        />
        <Legend />
        {models.map((model, i) => (
          <Line
            key={model}
            type="monotone"
            dataKey={model}
            stroke={COLORS[i % COLORS.length]}
            strokeWidth={2}
            dot={false}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  )
}
